import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import cx from 'classnames';
import { useTable, useSortBy, useExpanded, useGlobalFilter } from 'react-table';
import { Table } from 'react-bootstrap';
import { useForecast } from '../../providers/ForecastProvider';
import { useProjection } from '../../providers/ProjectionProvider';
import { calculateForecastQuantity, calculateForecastRevenue, calculateRevenueGrowth } from '../../utils/calculation';
import { hideLoading, showLoading } from '../../lib/uiService';
import TableHead from '../../components/common/TableHead';
import TableBody from '../../components/common/TableBody';
import { useProductProjectionsTableColumns } from './ProductProjectionsTableHooks';
import { toast } from 'react-toastify';

const ProductProjectionsTable: React.FC<any> = ({ projection }: { projection: any }) => {
  const { id: projectionId } = useParams();

  const { forecastId, territoryId, hasForecasts, forecastCalendarId } = useForecast();
  const [productForecasts, setProductForecasts] = useState<any[]>([]);
  const [initialData, setInitialData] = useState<any[]>([]);
  const projectionContext = useProjection();
  const [isDirty, setIsDirty] = useState(false);
  const [isAutoSave, setIsAutoSave] = useState(false);

  useEffect(() => {
    const localStorageCalendarId = localStorage.getItem('timePeriod') || '';
    const calendarId = forecastCalendarId ? forecastCalendarId : localStorageCalendarId;
    if (hasForecasts && forecastId && calendarId && territoryId) {
      if (projection) {
        const changes = projection.getProductChanges();
        // filter changes by territoryId and calendarId
        const filteredChanges = changes.filter((change: any) => {
          return (
            change.forecastProduct.territoryId === territoryId &&
            change.forecastProduct.forecastCalendarId === calendarId
          );
        });
        setInitialData(filteredChanges);
        setProductForecasts(filteredChanges);
      } else {
        setInitialData([]);
        setProductForecasts([]);
      }
    }
  }, [forecastId, territoryId, forecastCalendarId, projection]);

  useEffect(() => {
    if (isAutoSave) handleSave();
  }, [productForecasts]);

  const handleChange = (id: string, updatedData: any) => {
    setProductForecasts((prevData) => {
      return prevData.map((row) => {
        if (row.forecastProductProjectionId === id) {
          const newRow = {
            ...row,
            ...updatedData,
          };
          const newForecastRevenue = calculateForecastRevenue(
            row.forecastProduct.revenue,
            row.forecastProduct.salesGrowth,
            row.forecastProduct.priceGrowth,
            newRow.salesGrowth,
            newRow.priceGrowth,
          );
          const newRevenueGrowth = calculateRevenueGrowth(newRow['salesGrowth'], newRow['priceGrowth']);
          if (Object.keys(updatedData).includes('salesGrowth')) {
            const newForecastQuantity = calculateForecastQuantity(
              row.forecastProduct.quantity,
              row.forecastProduct.salesGrowth,
              newRow.salesGrowth,
            );
            return {
              ...newRow,
              quantity: newForecastQuantity,
              revenue: newForecastRevenue,
              revenueGrowth: newRevenueGrowth,
            };
          } else {
            return {
              ...newRow,
              revenue: newForecastRevenue,
            };
          }
        } else {
          return row;
        }
      });
    });
    setIsDirty(true);
  };

  const handleRemove = (id: string) => {
    setProductForecasts((prevData) => {
      return prevData.filter((row) => row.forecastProductProjectionId !== id);
    });
    setIsAutoSave(true);
  };

  const handleSave = () => {
    showLoading();
    const changedRows = productForecasts.filter((productForecast, index) => {
      return (
        productForecast.salesGrowth !== initialData[index].salesGrowth ||
        productForecast.priceGrowth !== initialData[index].priceGrowth
      );
    });
    if (changedRows.length) {
      const data = changedRows.map(({ forecastProductId, salesGrowth, priceGrowth }) => ({
        forecastProductId,
        salesGrowth,
        priceGrowth,
      }));

      projectionContext.handlers
        .update({
          territoryId,
          projectionId,
          isExec: false,
          data,
        })
        .then(() => {
          hideLoading();
          setInitialData(productForecasts);
          toast.success('Successfully Updated Product Projection');
        })
        .catch((error) => {
          hideLoading();
          console.log(error);
        });
    }
    // find removed rows
    const removedRows = initialData.filter((initialRow) => {
      return !productForecasts.find(
        (row) => row.forecastProductProjectionId === initialRow.forecastProductProjectionId,
      );
    });
    if (removedRows.length) {
      const data = removedRows.map(({ forecastProductProjectionId }) => ({
        forecastProductProjectionId,
      }));
      projectionContext.handlers
        .discardLine({
          territoryId,
          projectionId,
          data,
        })
        .then(() => {
          projectionContext.handlers.fetch(forecastId);
          hideLoading();
          setInitialData(productForecasts);
          toast.success('Successfully discarded Product Projection');
        })
        .catch((error) => {
          hideLoading();
          console.log(error);
        });
    }
    setIsDirty(false);
    setIsAutoSave(false);
  };

  const handleDiscardAll = () => {
    productForecasts.forEach((row) => {
      handleRemove(row.forecastProductProjectionId);
    });
    setIsAutoSave(true);
  };
  const handleSaveItem = React.useCallback(
    (changedData?: any) => {
      const changedRows = changedData;

      if (changedRows.length === 0) return;
      const data = changedRows.map(({ forecastProductId, salesGrowth, priceGrowth, explanation, quantity }: any) => ({
        forecastProductId,
        salesGrowth,
        priceGrowth,
        explanation,
        quantity,
      }));
      showLoading();
      projectionContext.handlers
        .update({
          territoryId,
          forecastId,
          isExec: false,
          data,
        })
        .then(() => {
          hideLoading();
          setInitialData(productForecasts);
          toast.success('Successfully Updated Product Projection');
        })
        .catch((error) => {
          hideLoading();
          console.log(error);
        });
      hideLoading();
    },
    [productForecasts],
  );

  const columns = useProductProjectionsTableColumns({ handleChange, handleRemove, handleSaveItem });

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: useMemo(() => productForecasts, [productForecasts]),
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
  );

  return (
    <div className="card shadow" style={{ marginBottom: '44px' }}>
      <div className="card-header py-3">
        <p className="text-primary m-0 fw-bold">Product Projections</p>
      </div>
      <div className="card-body">
        <div>
          <Table striped className="table-sm" bordered {...getTableProps()} responsive>
            <TableHead headerGroups={headerGroups} />
            <TableBody
              {...getTableBodyProps()}
              rows={rows}
              renderRow={(row: any, key: number) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={key} className={`table-body-font-size`}>
                    {row.cells.map((cell: any, index: number) => {
                      return (
                        <td
                          {...cell.getCellProps()}
                          key={`${key}-${index}`}
                          style={{
                            border: 'none',
                            backgroundColor: '#fff',
                          }}
                          className={'text-dark align-middle'}
                        >
                          {cell.render('Cell')}
                        </td>
                      );
                    })}
                  </tr>
                );
              }}
            />
            <tfoot>
              <tr>
                {columns.map((column: any, index: number) => {
                  return (
                    <td key={index} className="cursor-pointer">
                      {column?.Footer?.({ data: productForecasts })}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          </Table>
        </div>
        <div className="row">
          <div className="col-6">
            <button
              disabled={productForecasts.length === 0}
              className="btn btn-secondary float-start"
              type="button"
              onClick={handleDiscardAll}
            >
              Discard All Product Projections
            </button>
          </div>
          <div className="col-6">
            <button
              className={cx('btn float-end', isDirty ? 'btn-primary' : 'btn-secondary')}
              type="button"
              onClick={handleSave}
              disabled={!isDirty}
            >
              Update
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductProjectionsTable;
